/**
 * Identidad visual de una persona cuando NO hay foto: iniciales + un color
 * estable.
 *
 * Es la ÚNICA implementación de estas tres funciones en el monorepo. Antes cada
 * pantalla resolvía las iniciales por su cuenta con `charAt(0)` y reglas
 * distintas (unas caían al email, otras no; unas pasaban a mayúsculas, otras
 * no), así que la misma persona se veía distinta según dónde la miraras.
 *
 * Puro y sin dependencias a propósito: lo usan la web, el escritorio, el móvil
 * y los tests, y tiene que dar EXACTAMENTE el mismo resultado en todos.
 */

/** Cuántas iniciales lleva el avatar de reserva. */
const MAX_INITIALS = 2;

/** Cuántas parejas de color tiene la paleta (ver `avatar.tsx`). */
export const AVATAR_PALETTE_SIZE = 8;

/**
 * Iniciales para el avatar de reserva.
 *
 * Toma hasta dos, de la primera y la última palabra del nombre. Si no hay
 * nombre cae al email, y si tampoco lo hay, a un punto medio (`·`) — nunca a
 * una interrogación, que se lee como error.
 *
 * Usa `codePointAt` y no `[0]`: un nombre que empieza por un emoji o por un
 * carácter fuera del plano básico se partiría en dos mitades ilegibles con el
 * índice directo, porque JavaScript indexa por unidad UTF-16.
 */
export function initialsOf(name?: string | null, email?: string | null): string {
  const source = (name ?? "").trim() || (email ?? "").trim().split("@")[0] || "";
  const parts = source.split(/[\s._-]+/u).filter(Boolean);
  if (parts.length === 0) {
    return "·";
  }
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1) ?? "") : "";
  return [first, last]
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((part) => String.fromCodePoint(part.codePointAt(0) ?? 0).toLocaleUpperCase())
    .join("");
}

/**
 * Índice de color estable para una persona, en `[0, AVATAR_PALETTE_SIZE)`.
 *
 * Deriva del **userId y no del nombre**: el nombre cambia (se corrige una
 * errata, se pone el apellido) y el color no debe cambiar con él, o la persona
 * "se convierte en otra" a ojos de quien la reconocía por el color.
 *
 * FNV-1a de 32 bits: barato, sin dependencias y con buena dispersión para
 * cadenas cortas. No es criptográfico ni pretende serlo.
 */
export function avatarPaletteIndex(userId?: string | null): number {
  // OJO: no convertir esto en un parámetro por defecto
  // (`userId: string | null = ""`). El `oxlint --fix` del pre-commit lo intenta
  // vía `unicorn/prefer-default-parameters`, y sería un CAMBIO SEMÁNTICO: los
  // valores por defecto solo se aplican a `undefined`, así que un `null`
  // —que es justo lo que llega de una fila sin usuario— pasaría de largo y
  // reventaría en `.length`.
  const key = typeof userId === "string" ? userId : "";

  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < key.length; i++) {
    // `charCodeAt` y NO `codePointAt`: se quiere recorrer unidades UTF-16 de
    // una en una. `codePointAt` devuelve el punto de código COMPLETO en los
    // pares suplentes (y `undefined` al pasarse del final), así que cambiaría
    // el hash y, con él, el color de gente que ya tenía uno asignado. El
    // `oxlint --fix` del pre-commit también intenta esta sustitución
    // (`unicorn/prefer-code-point`); aquí está mal.
    // oxlint-disable-next-line unicorn/prefer-code-point
    hash ^= key.charCodeAt(i);
    // `Math.imul` mantiene la multiplicación en 32 bits; con `*` se pierde
    // precisión al pasar de 2^53 y el hash deja de ser el mismo en todas
    // las plataformas.
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return Math.abs(hash) % AVATAR_PALETTE_SIZE;
}

/**
 * URL absoluta de un avatar a partir de la CLAVE que guarda `user.image`.
 *
 * En la fila se guarda la clave (`avatars/{userId}/{hash}.webp`) y no una URL
 * absoluta, para que la fila sea portable entre dev/rc/prod —que tienen
 * distinto origen de API— y para que un cambio de dominio no obligue a
 * reescribir la base de datos.
 *
 * Tolera que el valor guardado ya sea una URL absoluta: así una fila escrita
 * por una versión anterior no se ve rota.
 */
export function avatarUrl(key?: string | null, apiBaseUrl?: string | null): string | null {
  const value = (key ?? "").trim();
  if (!value) {
    return null;
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const base = (apiBaseUrl ?? "").replace(/\/+$/u, "");
  return `${base}/api/avatar/${value}`;
}
